const EXPIRY_MS = 1500;

/**
 * Ensure active link (the one that matches the current path) is visible on screen. Useful for long
 * navigation lists within scrolling panels.
 */
export const ensureActiveNavLinkVisibleViewFn = (
  container: HTMLElement,
  {
    block = "center",
    id,
    minThreshold,
  }: { block: ScrollIntoViewOptions["block"]; id: string; minThreshold: number | undefined },
) => {
  const storageKey = `hkActiveNavScroll--${id}`;
  const currentPath = window.location.pathname;
  const match = container.querySelector<HTMLAnchorElement>(`a[href="${currentPath}"]`);

  if (match) {
    scrollToMatch({ block, container, match, minThreshold, storageKey });
  }

  // Record scroll position in sessionStorage when user clicks on a link within our scope.
  const onNavClick = (event: MouseEvent) => {
    const target = event.target as Element | null;
    const anchor = target?.closest && target.closest("a");
    // Only store for same-origin links
    if (!anchor || anchor.origin !== window.location.origin) {
      return;
    }

    sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        scrollTop: container.scrollTop,
        timestamp: Date.now(),
      }),
    );
  };

  container.addEventListener("click", onNavClick);

  return {
    destroy: () => {
      container.removeEventListener("click", onNavClick);
    },
  };
};

function scrollToMatch({
  block = "center",
  container,
  match,
  minThreshold,
  storageKey,
}: {
  block: ScrollIntoViewOptions["block"];
  container: HTMLElement;
  match: HTMLAnchorElement;
  minThreshold: number | undefined;
  storageKey: string;
}) {
  let scrollPosition: number | undefined = undefined;

  // If we recently clicked a link to this path, prefer its stored scrollTop
  try {
    const { scrollTop, timestamp } = JSON.parse(sessionStorage.getItem(storageKey) ?? "") as {
      scrollTop: number;
      timestamp: number;
    };
    const delta = Date.now() - timestamp;
    if (delta <= EXPIRY_MS) {
      scrollPosition = scrollTop;
      sessionStorage.removeItem(storageKey);
    }
  } catch (_err) {
    // Ignore parse/storage errors and continue with calculated behavior
  }
  if (scrollPosition === undefined) {
    const linkTop = match.offsetTop;
    const linkHeight = match.offsetHeight;
    const containerHeight = container.clientHeight;
    const currentScroll = container.scrollTop;

    switch (block) {
      case "start":
        if (minThreshold && currentScroll === 0 && minThreshold > linkTop) {
          break;
        }
        scrollPosition = linkTop - 40;
        break;
      case "end":
        scrollPosition = linkTop - containerHeight + linkHeight;
        break;
      case "nearest":
        // Only scroll if not already visible
        const linkBottom = linkTop + linkHeight;
        const containerBottom = currentScroll + containerHeight;

        if (linkTop < currentScroll) {
          scrollPosition = linkTop;
        } else if (linkBottom > containerBottom) {
          scrollPosition = linkTop - containerHeight + linkHeight;
        }
        break;
      case "center":
      default:
        scrollPosition = linkTop - containerHeight / 2 + linkHeight / 2;
        break;
    }
  }

  if (scrollPosition) {
    container.scrollTop = scrollPosition;
  }
}
